import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const reducedMotionQuery = '(prefers-reduced-motion: reduce)';

function prefersReducedMotion(): boolean {
    return window.matchMedia(reducedMotionQuery).matches;
}

function setText(selector: string, value: string | undefined): void {
    const element = document.querySelector<HTMLElement>(selector);
    if (element && value !== undefined && element.textContent !== value) {
        element.textContent = value;
    }
}

function setStoryVisualFromStep(step: HTMLElement): void {
    const mode = step.dataset.storyDiagramMode ?? 'system';
    const focusByMode: Record<string, string> = {
        system: 'life',
        craft: 'craft',
        loop: 'tech',
        space: 'body',
        ikigai: 'center'
    };

    const story = document.querySelector<HTMLElement>('[data-about-story]');
    const diagram = document.querySelector<HTMLElement>('[data-story-diagram]');
    const symbol = step.dataset.storySymbol ?? '';
    const tone = step.dataset.storyTone ?? 'vermilion';

    story?.setAttribute('data-story-tone', tone);
    diagram?.setAttribute('data-mode', mode);
    diagram?.setAttribute('data-focus', focusByMode[mode] ?? '');

    setText('[data-story-symbol]', symbol);
    setText('[data-story-kicker]', step.dataset.storyKicker);
    setText('[data-story-panel-kicker]', step.dataset.storyKicker);
    setText('[data-story-panel-title]', step.dataset.storyTitle);
    setText('[data-story-panel-body]', step.dataset.storyBody);
}

function initAboutReveals(page: HTMLElement): void {
    const targets = Array.from(page.querySelectorAll<HTMLElement>('[data-about-reveal]'));
    if (!targets.length) {
        return;
    }

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
        targets.forEach(target => target.classList.add('is-visible'));
        return;
    }

    const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (!entry.isIntersecting) {
                return;
            }

            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
        });
    }, {
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.18
    });

    targets.forEach(target => observer.observe(target));
}

function initAboutHeroIntro(page: HTMLElement): void {
    const hero = page.querySelector<HTMLElement>('[data-about-hero]');
    if (!hero) {
        return;
    }

    const revealItems = Array.from(hero.querySelectorAll<HTMLElement>('[data-hero-reveal]'));
    const visual = hero.querySelector<HTMLElement>('[data-hero-visual]');
    const portrait = hero.querySelector<HTMLElement>('[data-hero-portrait]');
    const shapes = Array.from(hero.querySelectorAll<HTMLElement>('.about-hero__shape, .about-hero__sun, .about-hero__seal, .about-hero__kanji'));

    if (prefersReducedMotion()) {
        page.classList.add('is-reduced-motion');
        page.style.setProperty('--hero-who-progress', '1');
        revealItems.forEach(item => item.classList.add('is-visible'));
        visual?.classList.add('is-visible');
        return;
    }

    page.style.setProperty('--hero-who-progress', '0');

    gsap.set(revealItems, { autoAlpha: 0, y: 28 });
    gsap.set(visual, { autoAlpha: 0, scale: 0.96, y: 24 });
    gsap.set(portrait, { autoAlpha: 0, scale: 1.08, y: 36, rotate: 1.5 });
    gsap.set(shapes, { autoAlpha: 0, scale: 0.9 });

    let heroSettled = false;
    const settleHero = (): void => {
        if (heroSettled) {
            return;
        }

        heroSettled = true;
        revealItems.forEach(item => item.classList.add('is-visible'));
        visual?.classList.add('is-visible');
        gsap.set(revealItems, { autoAlpha: 1, y: 0 });
        gsap.set(visual, { autoAlpha: 1, scale: 1, y: 0 });
        gsap.set(portrait, { autoAlpha: 1, scale: 1, y: 0, rotate: 0 });
        gsap.set(shapes, { autoAlpha: 1, scale: 1 });
    };

    const timeline = gsap.timeline({
        defaults: { ease: 'power3.out' },
        onComplete: settleHero
    });

    timeline
        .to(visual, { autoAlpha: 1, scale: 1, y: 0, duration: 0.8 }, 0.05)
        .to(portrait, { autoAlpha: 1, scale: 1, y: 0, rotate: 0, duration: 1.05 }, 0.18)
        .to(shapes, { autoAlpha: 1, scale: 1, duration: 0.8, stagger: 0.08 }, 0.24)
        .to(revealItems, { autoAlpha: 1, y: 0, duration: 0.72, stagger: 0.11 }, 0.28);

    window.setTimeout(settleHero, 1800);

    if (visual) {
        gsap.to(visual, {
            y: 52,
            ease: 'none',
            scrollTrigger: {
                trigger: hero,
                start: 'top top',
                end: 'bottom top',
                scrub: true
            }
        });
    }

    if (portrait) {
        gsap.to(portrait, {
            scale: 1.035,
            ease: 'none',
            scrollTrigger: {
                trigger: hero,
                start: 'top top',
                end: 'bottom top',
                scrub: true
            }
        });
    }

    ScrollTrigger.create({
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        scrub: true,
        onUpdate: self => {
            page.style.setProperty('--hero-who-progress', self.progress.toFixed(4));
        }
    });
}

function initAboutStoryScroll(page: HTMLElement): () => void {
    const story = page.querySelector<HTMLElement>('[data-about-story]');
    const steps = Array.from(page.querySelectorAll<HTMLElement>('[data-story-step]'));
    const progress = page.querySelector<HTMLElement>('[data-story-progress]');

    if (!story || !steps.length) {
        return () => undefined;
    }

    let activeStep = steps[0];

    const activate = (step: HTMLElement): void => {
        activeStep = step;
        steps.forEach(candidate => candidate.classList.toggle('is-active', candidate === step));
        setStoryVisualFromStep(step);
        page.dispatchEvent(new CustomEvent('about-story-active', { detail: { id: step.id } }));
    };

    activate(activeStep);

    if (prefersReducedMotion()) {
        steps.forEach(step => step.classList.add('is-active'));
        progress?.style.setProperty('transform', 'scaleY(1)');
        return () => setStoryVisualFromStep(activeStep);
    }

    ScrollTrigger.create({
        trigger: story,
        start: 'top 70%',
        end: 'bottom 35%',
        onEnter: () => page.classList.add('is-story-visible'),
        onEnterBack: () => page.classList.add('is-story-visible'),
        onLeave: () => page.classList.remove('is-story-visible'),
        onLeaveBack: () => page.classList.remove('is-story-visible')
    });

    steps.forEach(step => {
        ScrollTrigger.create({
            trigger: step,
            start: 'top 58%',
            end: 'bottom 42%',
            onEnter: () => activate(step),
            onEnterBack: () => activate(step)
        });
    });

    if (progress) {
        ScrollTrigger.create({
            trigger: story,
            start: 'top top',
            end: 'bottom bottom',
            scrub: true,
            onUpdate: self => {
                progress.style.transform = `scaleY(${Math.max(0.02, self.progress).toFixed(4)})`;
            }
        });
    }

    window.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
    return () => setStoryVisualFromStep(activeStep);
}

function initAboutStoryTracker(page: HTMLElement): void {
    const steps = Array.from(page.querySelectorAll<HTMLElement>('[data-story-step]'));
    if (!steps.length) {
        return;
    }

    page.querySelector('.about-story-tracker')?.remove();

    const tracker = document.createElement('nav');
    tracker.className = 'about-story-tracker';
    tracker.setAttribute('aria-label', 'Story chapters');

    const list = document.createElement('ol');
    tracker.appendChild(list);

    steps.forEach((step, index) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        const label = document.createElement('span');

        link.href = `#${step.id}`;
        link.className = 'about-story-tracker__link';
        link.dataset.storyTarget = step.id;
        link.setAttribute('aria-label', step.dataset.storyLabel ?? `Chapter ${index + 1}`);
        label.textContent = step.dataset.storyLabel ?? `${index + 1}`;

        link.appendChild(label);
        item.appendChild(link);
        list.appendChild(item);

        link.addEventListener('click', event => {
            event.preventDefault();
            step.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
        });
    });

    page.appendChild(tracker);

    const links = Array.from(tracker.querySelectorAll<HTMLElement>('[data-story-target]'));
    const setActive = (id: string): void => {
        links.forEach(link => {
            const isActive = link.dataset.storyTarget === id;
            link.classList.toggle('is-active', isActive);
            if (isActive) {
                link.setAttribute('aria-current', 'true');
            } else {
                link.removeAttribute('aria-current');
            }
        });
    };

    setActive(steps[0].id);
    page.addEventListener('about-story-active', event => {
        const detail = (event as CustomEvent<{ id: string }>).detail;
        if (detail?.id) {
            setActive(detail.id);
        }
    });
}

function initAboutIkigaiInteractions(page: HTMLElement, restoreActiveStory: () => void): void {
    const items = Array.from(page.querySelectorAll<HTMLButtonElement>('[data-ikigai-key]'));
    const diagram = page.querySelector<HTMLElement>('[data-story-diagram]');
    if (!items.length || !diagram) {
        return;
    }

    const focusMap: Record<string, string> = {
        love: 'life',
        skill: 'craft',
        need: 'tech',
        value: 'body'
    };

    const activateItem = (item: HTMLButtonElement): void => {
        const key = item.dataset.ikigaiKey ?? '';
        diagram.dataset.mode = 'ikigai';
        diagram.dataset.focus = focusMap[key] ?? 'center';

        setText('[data-story-panel-kicker]', 'IKIGAI');
        setText('[data-story-panel-title]', item.dataset.ikigaiTitle);
        setText('[data-story-panel-body]', item.dataset.ikigaiBody);

        items.forEach(candidate => candidate.classList.toggle('is-active', candidate === item));
    };

    const restore = (): void => {
        items.forEach(candidate => candidate.classList.remove('is-active'));
        restoreActiveStory();
    };

    items.forEach(item => {
        item.addEventListener('mouseenter', () => activateItem(item));
        item.addEventListener('focus', () => activateItem(item));
        item.addEventListener('mouseleave', restore);
        item.addEventListener('blur', restore);
    });
}

document.addEventListener('DOMContentLoaded', () => {
    const page = document.querySelector<HTMLElement>('.about-page');
    if (!page) {
        return;
    }

    if (prefersReducedMotion()) {
        page.classList.add('is-reduced-motion');
    }

    initAboutReveals(page);
    initAboutHeroIntro(page);
    const restoreActiveStory = initAboutStoryScroll(page);
    initAboutStoryTracker(page);
    initAboutIkigaiInteractions(page, restoreActiveStory);
});
